import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
} from 'react';
import { SupportedLanguage, TranslationContext } from './types';
import { translationUtils } from './translationUtils';

const TranslationReactContext = createContext<TranslationContext | undefined>(
  undefined
);

interface TranslationProviderProps {
  children: ReactNode;
  defaultLanguage?: SupportedLanguage;
}

export const TranslationProvider: React.FC<TranslationProviderProps> = ({
  children,
  defaultLanguage = 'en',
}) => {
  const [currentLanguage, setCurrentLanguage] =
    useState<SupportedLanguage>(defaultLanguage);

  const setLanguage = useCallback((language: SupportedLanguage) => {
    setCurrentLanguage(language);
    translationUtils.setLanguage(language);
  }, []);

  const t = useCallback(
    (key: string, fallback?: string, params?: Record<string, string>) => {
      return translationUtils.translate(key, fallback, params);
    },
    []
  );
  const v = useCallback((key: string, fallback?: string) => {
    return translationUtils.getValue(key, fallback);
  }, []);

  // Initialize translation utils with current language
  React.useEffect(() => {
    translationUtils.setLanguage(currentLanguage);
  }, [currentLanguage]);

  const value: TranslationContext = {
    currentLanguage,
    setLanguage,
    t,
    v,
  };

  return (
    <TranslationReactContext.Provider value={value}>
      {children}
    </TranslationReactContext.Provider>
  );
};

/**
 * Hook to use translations in components
 */
export const useTranslation = (): TranslationContext => {
  const context = useContext(TranslationReactContext);
  if (!context) {
    throw new Error('useTranslation must be used within a TranslationProvider');
  }
  return context;
};

/**
 * Higher-order component for translations
 */
export const withTranslation = <P extends object>(
  Component: React.ComponentType<
    P & {
      t: (
        key: string,
        fallback?: string,
        params?: Record<string, string>
      ) => string;
    }
  >
) => {
  return (props: P) => {
    const { t } = useTranslation();
    return <Component {...props} t={t} />;
  };
};
